import { FC, useCallback, useEffect, useMemo } from 'react'
import events, { Event } from 'pubsub/events'
import eventsV2, { Event as EventV2 } from 'pubsub/eventsV2'
import { CoreClickData } from 'utils/sendCoreAPIEvent'
import { getUser, getUserLoggedInState, getUserStatus } from 'redux/reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IActivityType, IProperty } from 'models/Analytics'
import { TimeoutLogic } from './TimeoutLogic/TimeoutLogic'
import { setSelectedMessageId } from 'redux/reducers/messages'
import { setOpenModal } from 'redux/reducers/header'
import { Logger } from 'utils/logger'
import { CoreUnderivedEventData } from 'pubsub/core'
import { TealiumDerivedPageViewUrlEventData, TealiumUnderivedEventData, TealiumUnderivedPageViewEventData } from 'pubsub/tealium'
import { coreEventIsValid } from './util/coreEventIsValid'
import { useDerivedCoreEventData } from 'pubsub/hooks/useDerivedCoreEventData'
import { useDerivedTealiumEventData } from 'pubsub/hooks/useDerivedTealiumEventData'
import { Activity, Component } from '@cms-max/medicare-api-sdk'
import { DisplayToasts } from './DisplayToasts'
import { createAndEmitLoadedEvent } from 'utils/onLoadEvents/create-and-emit-loaded-event'
import openLiveChat from 'utils/openLiveChat'
import { LanguageString } from 'models/Language'
import { CmsOpenLiveChatIncorrectFieldsError, isCmsOpenLiveChatEvent, isErrorWithCmsErrorHandler } from './util/CmsOpenLiveChatEvent'
import { TealiumPageViewEvent, TealiumStandardEvent } from '@cms-max/cms-browser-api-client'

export interface GlobalHeaderEvents {
  /** version 1 */
  sendSiteActivityMetrics: (activity: IActivityType, property: IProperty, component: string, clickData?: CoreClickData) => void
  /** version 2 */
  handleEvent: (event: { core?: CoreUnderivedEventData, tealium?: TealiumUnderivedEventData | TealiumUnderivedPageViewEventData }) => void
}

export interface GlobalHeaderModalEvents {
  openMessageCenter: (messageId?: string) => void,
  openHeaderModal: (DropdownName: string) => void
}

interface GlobalVariablesProps {
  timeoutEnabled?: boolean,
  displayToasts?: boolean
}

const isTealiumPageViewEvent = (event: TealiumStandardEvent | TealiumPageViewEvent): event is TealiumPageViewEvent => {
  return (event as TealiumPageViewEvent).site_section !== undefined
}

export const BrowserApi: FC<GlobalVariablesProps> = ({ timeoutEnabled, displayToasts }) => {
  // Site Activity Metrics
  const { t, i18n } = useTranslation('common')
  const isLoggedIn = useSelector(getUserLoggedInState)

  const dispatch = useDispatch()
  const user = useSelector(getUser)
  const status = useSelector(getUserStatus)

  const userIsFinishedLoading = useMemo(() => status === 'finished' || status === 'error', [status])

  const openMessageCenter = useCallback((messageID: string = '') => {
    if (user) {
      dispatch(setSelectedMessageId(messageID))
    } else {
      Logger.error('Beneficiary must be logged in to open the message center')
    }
  }, [dispatch, user])

  const sendSiteActivityMetrics = useCallback(
    (
      activity: IActivityType,
      property: IProperty,
      component: string,
      clickData?: CoreClickData
    ) => {
      const validation = coreEventIsValid({
        // Although activity doesn't actually match the enum, the Activity enum stores as strings, so we can cast it
        activity: activity as Activity,
        property,
        component: component as Component,
        coreClickData: clickData ?? {}
      })

      if (validation.valid) {
        // Create event and publish
        const { linkText, linkUrl } = clickData ?? { linkText: undefined, linkUrl: undefined }
        const event: Event = {
          // passed values
          activity,
          text: linkText ?? '',
          targetLocation: linkUrl,
          property,
          component,
          // derived from app state
          authenticated: isLoggedIn,
          language: i18n?.language ?? '',
          url: window.location.href,
          title: document.title,
          // static
          category: 'MAX',
          sendToTealium: false,
          sendToCoreAPI: true,
          isMessageCenter: false
        }
        events.publish(event)
      } else {
        Logger.error('Invalid parameters passed to sendSiteActivityMetrics:', validation.results)
      }
    },
    [i18n, isLoggedIn]
  )

  const coreDerivedEventData = useDerivedCoreEventData()
  const tealiumDerivedEventData = useDerivedTealiumEventData()

  const handleEvent = useCallback((eventObj: {
    core?: CoreUnderivedEventData,
    tealium?: TealiumUnderivedEventData | TealiumUnderivedPageViewEventData
  }) => {
    const { core, tealium } = eventObj
    // Validation
    const validation = core ? coreEventIsValid(core) : { valid: true, results: {} }
    if (!validation.valid) {
      Logger.error('Invalid parameters passed to handleEvent:', validation.results)
      return
    }
    // Add derived fields
    const newEvent: EventV2 = {}
    if (core) {
      newEvent.core = {
        ...coreDerivedEventData,
        ...core,
        additionalData: core.additionalData
      }
    }
    if (tealium) {
      const url = new URL(window.location.href)
      const tealiumDerivedPageViewUrlEventData: TealiumDerivedPageViewUrlEventData = {
        page: url.pathname,
        site_domain: url.host,
        // TODO: We should derive this from the url probably but it's a pain and will have a lot of test cases. The main downside of doing this is if we have env variables set incorrectly in an environment or local
        site_environment: process.env.REACT_APP_MY_MEDICARE_ENV!
      }
      if (isTealiumPageViewEvent(tealium)) {
        newEvent.tealium = {
          ...tealiumDerivedEventData.derivedPageViewData,
          ...tealiumDerivedPageViewUrlEventData,
          ...tealium,
          additionalData: tealium.additionalData
        }
      } else {
        newEvent.tealium = {
          ...tealiumDerivedEventData.derivedData,
          ...tealium,
          additionalData: tealium.additionalData
        }
      }
    }

    eventsV2.publish(newEvent)
  },
  [coreDerivedEventData, tealiumDerivedEventData])

  // OPEN HEADER MODAL
  const openHeaderModal = useCallback((modalName: 'Chat') => {
    // attempt to translate in case we are in spanish
    const modalNameTranslated = t(modalName)
    dispatch(setOpenModal(modalNameTranslated))
  }, [dispatch, t])

  // OPEN LIVE CHAT
  const openLiveChatEventListener = useCallback(async (openLiveChatEvent: any) => {
    // Only use options if we can confirm the passed event looks correct
    if (isCmsOpenLiveChatEvent(openLiveChatEvent)) {
      try {
        await openLiveChat({
          isAuthenticated: isLoggedIn,
          asPopup: openLiveChatEvent.detail.asPopup ?? false,
          lang: i18n.language as LanguageString
        })
        return openLiveChatEvent.detail.callback?.()
      } catch (e: unknown) {
        return openLiveChatEvent.detail.callback?.(e)
      }
    } else {
      const error = new CmsOpenLiveChatIncorrectFieldsError('cms-open-live-chat', openLiveChatEvent)
      Logger.error('Received invalid event to openLiveChatEventListener, unable to open live chat', error)
      if (isErrorWithCmsErrorHandler(openLiveChatEvent)) {
        return openLiveChatEvent.detail.callback?.(error)
      }
    }
  }, [isLoggedIn, i18n.language])

  useEffect(() => {
    const eventName = 'cms-open-live-chat'
    document.addEventListener(eventName, openLiveChatEventListener)
    return () => {
      document.removeEventListener(eventName, openLiveChatEventListener)
    }
  }, [openLiveChatEventListener])

  // Loaded Event
  useEffect(() => {
    if (userIsFinishedLoading) {
      createAndEmitLoadedEvent()
    }
  }, [userIsFinishedLoading])

  // INITIALIZE GLOBAL HEADER EVENTS AND CH TRIGGERS
  useEffect(() => {
    window.globalHeaderEvents = {
      sendSiteActivityMetrics,
      handleEvent
    }
    window.chTriggers = {
      openMessageCenter,
      openHeaderModal
    }
  }, [sendSiteActivityMetrics, openMessageCenter, openHeaderModal, handleEvent])

  // unmount
  useEffect(() => {
    return () => {
      window.globalHeaderEvents = undefined
      window.chTriggers = undefined
    }
  }, [])

  return (
    <>
      {timeoutEnabled && <TimeoutLogic />}
      {displayToasts && <DisplayToasts />}
    </>
  )
}
